import React, { useState, useCallback } from 'react'
import { Button, Empty, message, Progress, Space, Tabs, Typography } from 'antd'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useCompany } from '../../../contexts/CompanyContext'
import { useConfig } from '../../../contexts/ConfigContext'
import { useRegister } from '../../../contexts/RegisterContext'
import { useSecureRequest } from '../../../hooks/useSecureRequest'
import { IMovementRS } from '../../../models/salesModel'
import ReportFilters from './ReportFilters'
import SalesByPeriodReport, { periodExcelColumns, getPeriodExcelData } from './SalesByPeriodReport'
import SalesBySellerReport, { sellerExcelColumns, getSellerExcelData } from './SalesBySellerReport'
import SalesByCustomerReport, { customerExcelColumns, getCustomerExcelData } from './SalesByCustomerReport'
import SalesByPaymentReport, { paymentExcelColumns, getPaymentExcelData } from './SalesByPaymentReport'
import TopProductsReport, { productExcelColumns, getProductExcelData } from './TopProductsReport'
import SalesByPosReport, { posExcelColumns, getPosExcelData } from './SalesByPosReport'
import { downloadBase64File } from '../../../utils/excelExport'

const { Title } = Typography


type TReportTab = 'period' | 'seller' | 'customer' | 'payment' | 'products' | 'pos'

function ViewSalesReports() {
  const { company } = useCompany()
  const { config } = useConfig()
  const { register } = useRegister()
  const { securePost } = useSecureRequest()

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('day'),
    dayjs().endOf('day')
  ])
  const [activeTab, setActiveTab] = useState<TReportTab>('period')
  const [movements, setMovements] = useState<IMovementRS[]>([])
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const [sellerFilter, setSellerFilter] = useState('ALL')
  const [customerFilter, setCustomerFilter] = useState('ALL')
  const [posFilter, setPosFilter] = useState('ALL')

  const fetchAllMovements = useCallback(async () => {
    if (!company.current?.uid) return

    setLoading(true)
    setProgress(0)
    setMovements([])
    setHasSearched(true)

    try {
      const startDate = dateRange[0].startOf('day').toDate().getTime()
      const endDate = dateRange[1].endOf('day').toDate().getTime()

      // 1 Sola Petición HTTP al endpoint agregador de ventas en backend
      const res = await securePost('/sale/reports/get-sales-report-summary', {
        company: company.current.uid,
        startDate,
        endDate,
        seller: sellerFilter !== 'ALL' ? sellerFilter : undefined,
        customer: customerFilter !== 'ALL' ? customerFilter : undefined,
        pos: posFilter !== 'ALL' ? posFilter : undefined,
        module: config.module
      })

      if (res?.status === 200) {
        setMovements(res.sales || [])
        setProgress(100)
        if (!res.sales || res.sales.length === 0) {
          message.info('No se encontraron registros de ventas para el período seleccionado')
        } else {
          message.success(`Se cargaron ${res.sales.length} facturas de venta`)
        }
      } else {
        message.error(res?.message || 'No fue posible consultar el informe de ventas')
      }
    } catch (error) {
      console.error('Error fetching sales report summary:', error)
      message.error('Ocurrió un error al generar el informe de ventas')
    } finally {
      setLoading(false)
    }
  }, [company.current?.uid, config.module, customerFilter, dateRange, posFilter, securePost, sellerFilter])

  const handleExport = async () => {
    if (movements.length === 0) {
      message.warning('No hay datos para exportar')
      return
    }

    try {
      const dateLabel = `${dateRange[0].format('YYYYMMDD')}_${dateRange[1].format('YYYYMMDD')}`
      let rows: any[] = []
      let columns: any[] = []

      switch (activeTab) {
        case 'period':
          rows = getPeriodExcelData(movements, register.paymentOptions)
          columns = periodExcelColumns
          break
        case 'seller':
          rows = getSellerExcelData(movements)
          columns = sellerExcelColumns
          break
        case 'customer':
          rows = getCustomerExcelData(movements)
          columns = customerExcelColumns
          break
        case 'payment':
          rows = getPaymentExcelData(movements, register.paymentOptions)
          columns = paymentExcelColumns
          break
        case 'products':
          rows = getProductExcelData(movements)
          columns = productExcelColumns
          break
        case 'pos':
          rows = getPosExcelData(movements)
          columns = posExcelColumns
          break
      }

      // Solicitud cifrada al backend para generar Excel en servidor y retornar Base64
      const res = await securePost('/sale/reports/export-sales-excel', {
        company: company.current?.uid,
        reportType: activeTab,
        rows,
        columns,
        fileName: `Informe_Ventas_${activeTab}_${dateLabel}.xlsx`
      })

      if (res?.status === 200 && res.fileContentBase64) {
        downloadBase64File(res.fileContentBase64, res.fileName || `Informe_Ventas_${dateLabel}.xlsx`)
        message.success('Archivo Excel de ventas cifrado descargado exitosamente desde el servidor')
      } else {
        message.error('No se pudo generar el archivo Excel en el servidor')
      }
    } catch (err) {
      console.error('Error al exportar Excel de ventas:', err)
      message.error('Error al descargar el archivo Excel cifrado')
    }
  }

  // Filtrar solo facturas para informes que no incluyen notas
  const invoicesOnly = movements.filter(m => m.document_type === 'SALE_INVOICE')

  const tabItems = [
    {
      key: 'period',
      label: 'Por Período',
      children: <SalesByPeriodReport data={movements} loading={loading} />
    },
    {
      key: 'seller',
      label: 'Por Vendedor',
      children: <SalesBySellerReport data={invoicesOnly} loading={loading} />
    },
    {
      key: 'customer',
      label: 'Por Cliente',
      children: <SalesByCustomerReport data={invoicesOnly} loading={loading} />
    },
    {
      key: 'payment',
      label: 'Método de Pago',
      children: <SalesByPaymentReport data={invoicesOnly} loading={loading} />
    },
    {
      key: 'products',
      label: 'Productos Top',
      children: <TopProductsReport data={invoicesOnly} loading={loading} />
    },
    {
      key: 'pos',
      label: 'Por POS',
      children: <SalesByPosReport data={invoicesOnly} loading={loading} />
    }
  ]

  return (
    <div className="container-fluid">
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0, color: '#373738' }}>Informes de Ventas</Title>
      </div>

      <div className="card shadow" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ paddingTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <Space size="middle" wrap>
              <ReportFilters
                dateRange={dateRange}
                onDateChange={setDateRange}
                loading={loading}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={fetchAllMovements}
                loading={loading}
              >
                Generar Informe
              </Button>
            </Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={movements.length === 0 || loading}
              style={{ borderColor: '#52c41a', color: movements.length > 0 ? '#52c41a' : undefined }}
            >
              Exportar Excel
            </Button>
          </div>
        </div>

        <div className="card-body">
          {loading && progress < 100 && (
            <div style={{ marginBottom: 16 }}>
              <Progress
                percent={progress}
                status="active"
                strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                format={(percent) => `Cargando datos... ${percent}%`}
              />
            </div>
          )}

          {!hasSearched && !loading && (
            <Empty
              description="Seleccione un rango de fechas y presione 'Generar Informe' para visualizar los datos"
              style={{ padding: '60px 0' }}
            />
          )}

          {hasSearched && movements.length === 0 && !loading && (
            <Empty
              description="No se encontraron registros para el período seleccionado"
              style={{ padding: '60px 0' }}
            />
          )}

          {movements.length > 0 && (
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as TReportTab)}
              items={tabItems}
              size="large"
              type="card"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ViewSalesReports
